/**
 * XLSX Parser with Color Extraction
 * Reads XLSX XML directly to preserve cell colors
 */
import JSZip from 'jszip';

export async function parseXlsxWithColors(file) {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Read shared strings
  const sharedStringsXml = await zip.file('xl/sharedStrings.xml')?.async('string');
  const sharedStrings = sharedStringsXml ? parseSharedStrings(sharedStringsXml) : [];

  // Read styles for colors
  const stylesXml = await zip.file('xl/styles.xml')?.async('string');
  const styles = stylesXml ? parseXlsxStyles(stylesXml) : { fonts: [], fills: [], cellXfs: [] };

  // Find all sheet files
  const segments = [];
  let segId = 1;
  const sheetFiles = Object.keys(zip.files).filter(f => f.match(/xl\/worksheets\/sheet\d+\.xml/));

  for (const sheetFile of sheetFiles.sort()) {
    const sheetXml = await zip.file(sheetFile)?.async('string');
    if (!sheetXml) continue;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sheetXml, 'text/xml');
    const rows = doc.getElementsByTagName('row');

    for (const row of rows) {
      const rowNum = parseInt(row.getAttribute('r'));
      const cells = row.getElementsByTagName('c');

      for (const cell of cells) {
        const cellRef = cell.getAttribute('r');
        const cellType = cell.getAttribute('t');
        const styleIdx = parseInt(cell.getAttribute('s') || '0');
        const vEl = cell.getElementsByTagName('v')[0];
        const isEl = cell.getElementsByTagName('is')[0];

        let text = '';
        if (cellType === 's' && vEl) {
          // Shared string
          text = sharedStrings[parseInt(vEl.textContent)] || '';
        } else if (isEl) {
          // Inline string
          text = Array.from(isEl.getElementsByTagName('t')).map(t => t.textContent).join('');
        } else if (vEl) {
          text = vEl.textContent;
        }

        if (!text.trim()) continue;

        // Get formatting from style
        const cellXf = styles.cellXfs[styleIdx] || {};
        const font = styles.fonts[cellXf.fontId || 0] || {};
        const fill = styles.fills[cellXf.fillId || 0] || {};

        // Build sourceHtml
        const spanStyles = [];
        if (font.bold) spanStyles.push('font-weight:bold');
        if (font.italic) spanStyles.push('font-style:italic');
        if (font.underline) spanStyles.push('text-decoration:underline');
        if (font.size) spanStyles.push(`font-size:${font.size}pt`);
        if (font.color && !['000000', 'FF000000', '00000000'].includes(font.color)) {
          const c = font.color.length === 8 ? font.color.slice(2) : font.color;
          if (c !== '000000') spanStyles.push(`color:#${c}`);
        }
        if (fill.fgColor && !['000000', 'FF000000', '00000000', 'FFFFFFFF', '00FFFFFF'].includes(fill.fgColor)) {
          const c = fill.fgColor.length === 8 ? fill.fgColor.slice(2) : fill.fgColor;
          if (c && c !== '000000' && c !== 'FFFFFF') spanStyles.push(`background-color:#${c}`);
        }

        const sourceHtml = spanStyles.length > 0
          ? `<span style="${spanStyles.join(';')}">${text}</span>`
          : text;

        const colMatch = cellRef?.match(/([A-Z]+)(\d+)/);
        const colNum = colMatch ? colMatch[1].split('').reduce((a, c) => a * 26 + c.charCodeAt(0) - 64, 0) : 1;

        segments.push({
          id: segId++,
          source: text,
          sourceHtml,
          target: '',
          targetHtml: '',
          status: 'empty',
          hasColorFormatting: spanStyles.length > 0,
          formatting: {
            is_excel_cell: true,
            row: rowNum,
            column: colNum,
            cell_ref: cellRef,
            bold: font.bold || false,
            color: font.color || null,
            bg_color: fill.fgColor || null
          }
        });
      }
    }
  }

  const coloredSegs = segments.filter(s=>s.hasColorFormatting);
  console.log("[xlsxColorParser] segments:", segments.length, "with colors:", coloredSegs.length);
  if(coloredSegs.length > 0) console.log("[xlsxColorParser] first colored:", coloredSegs[0]);
  console.log("[xlsxColorParser] sample:", segments[0]);
  return segments;
}

function parseSharedStrings(xml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');
  const sis = doc.getElementsByTagName('si');
  return Array.from(sis).map(si =>
    Array.from(si.getElementsByTagName('t')).map(t => t.textContent).join('')
  );
}

function parseXlsxStyles(xml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');

  // Office theme color palette (approximations)
  const themeColors = {
    '0': 'FFFFFF', '1': '000000', '2': 'E7E6E6', '3': '44546A',
    '4': '4472C4', '5': 'ED7D31', '6': 'A9D18E', '7': 'FFC000',
    '8': '4472C4', '9': 'ED7D31'
  };

  // Helper to get color from element
  const getColor = (el) => {
    if (!el) return null;
    // Direct RGB value
    const rgb = el.getAttribute('rgb');
    if (rgb && !['FF000000','00000000','FF000000'].includes(rgb) && rgb !== 'FFFFFFFF') return rgb;
    // Theme color
    const theme = el.getAttribute('theme');
    if (theme !== null) {
      const tint = parseFloat(el.getAttribute('tint') || '0');
      const baseColor = themeColors[theme];
      if (baseColor && baseColor !== 'FFFFFF' && baseColor !== '000000') {
        return 'FF' + baseColor;
      }
    }
    // Indexed color (legacy)
    const indexed = el.getAttribute('indexed');
    if (indexed !== null) {
      const indexedColors = ['000000','FFFFFF','FF0000','00FF00','0000FF','FFFF00','FF00FF','00FFFF',
        '000000','FFFFFF','FF0000','00FF00','0000FF','FFFF00','FF00FF','00FFFF','800000','008000',
        '000080','808000','800080','008080','C0C0C0','808080','9999FF','993366','FFFFCC','CCFFFF',
        '660066','FF8080','0066CC','CCCCFF','000080','FF00FF','FFFF00','00FFFF','800080','800000',
        '008080','0000FF','00CCFF','CCFFFF','CCFFCC','FFFF99','99CCFF','FF99CC','CC99FF','FFCC99',
        '3366FF','33CCCC','99CC00','FFCC00','FF9900','FF6600','666699','969696','003366','339966',
        '003300','333300','993300','993366','333399','333333'];
      const idx = parseInt(indexed);
      if (idx < indexedColors.length && indexedColors[idx] !== '000000' && indexedColors[idx] !== 'FFFFFF') {
        return 'FF' + indexedColors[idx];
      }
    }
    return null;
  };

  // Parse fonts - use getElementsByTagName for better XML namespace compat
  const fonts = [];
  const fontsEl = doc.getElementsByTagName('fonts')[0];
  const fontEls = fontsEl ? Array.from(fontsEl.getElementsByTagName('font')) : [];
  for (const font of fontEls) {
    const colorEl = font.getElementsByTagName('color')[0];
    const sizeEl = font.getElementsByTagName('sz')[0];
    const bEl = font.getElementsByTagName('b')[0];
    const iEl = font.getElementsByTagName('i')[0];
    const uEl = font.getElementsByTagName('u')[0];
    fonts.push({
      bold: !!bEl,
      italic: !!iEl,
      underline: !!uEl,
      size: sizeEl ? parseFloat(sizeEl.getAttribute('val')) : null,
      color: getColor(colorEl)
    });
  }

  // Parse fills
  const fills = [];
  const fillsEl = doc.getElementsByTagName('fills')[0];
  const fillEls = fillsEl ? Array.from(fillsEl.getElementsByTagName('fill')) : [];
  for (const fill of fillEls) {
    const patternFill = fill.getElementsByTagName('patternFill')[0];
    const fgColor = patternFill?.getElementsByTagName('fgColor')[0];
    fills.push({
      fgColor: getColor(fgColor)
    });
  }

  // Parse cellXfs
  const cellXfs = [];
  const cellXfsEl = doc.getElementsByTagName('cellXfs')[0];
  const xfEls = cellXfsEl ? Array.from(cellXfsEl.getElementsByTagName('xf')) : [];
  for (const xf of xfEls) {
    cellXfs.push({
      fontId: parseInt(xf.getAttribute('fontId') || '0'),
      fillId: parseInt(xf.getAttribute('fillId') || '0'),
    });
  }

  console.log('[xlsxStyles] sample font:', fonts[0], fonts[1]);

  return { fonts, fills, cellXfs };
}

export default { parseXlsxWithColors };
